import React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import CloseIcon from "@mui/icons-material/Close";
import { GlobalWorkerOptions, getDocument } from "pdfjs-dist";

GlobalWorkerOptions.workerSrc = new URL("pdfjs-dist/build/pdf.worker.min.mjs", import.meta.url).toString();

export default function PdfViewerDialog({
  open,
  src,
  title,
  fileName,
  onClose,
}) {
  const canvasRef = React.useRef(null);
  const loadingTaskRef = React.useRef(null);
  const renderTaskRef = React.useRef(null);
  const pdfDocRef = React.useRef(null);
  const requestIdRef = React.useRef(0);
  const [numPages, setNumPages] = React.useState(0);
  const [page, setPage] = React.useState(1);
  const [scale, setScale] = React.useState(1);
  const [pdfDoc, setPdfDoc] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState("");

  const downloadProof = React.useCallback(async () => {
    try {
      const response = await fetch(src);
      if (!response.ok) throw new Error(`Proof download failed (${response.status})`);
      const objectUrl = URL.createObjectURL(await response.blob());
      const anchor = document.createElement("a");
      anchor.href = objectUrl;
      anchor.download = fileName || src.split("/").pop() || "proof";
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(objectUrl);
    } catch {
      window.open(src, "_blank", "noopener,noreferrer");
    }
  }, [fileName, src]);

  const cancelRenderTask = React.useCallback(() => {
    const task = renderTaskRef.current;
    if (task?.cancel) {
      try {
        task.cancel();
      } catch {}
    }
    renderTaskRef.current = null;
  }, []);

  const cleanupLoadedPdf = React.useCallback(async () => {
    const currentPdf = pdfDocRef.current;
    pdfDocRef.current = null;
    if (currentPdf?.destroy) {
      try {
        await currentPdf.destroy();
      } catch {}
    }
  }, []);

  const cleanupLoadingTask = React.useCallback(async () => {
    const loadingTask = loadingTaskRef.current;
    loadingTaskRef.current = null;
    if (loadingTask?.destroy) {
      try {
        await loadingTask.destroy();
      } catch {}
    }
  }, []);

  const renderPage = React.useCallback(async (pdf, pageNum, scl, requestId) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    cancelRenderTask();
    setBusy(true);
    let activeRenderTask = null;
    try {
      const pdfPage = await pdf.getPage(pageNum);
      if (requestId !== requestIdRef.current) return;
      const viewport = pdfPage.getViewport({ scale: scl });
      const dpr = window.devicePixelRatio || 1;
      const ctx = canvas.getContext("2d", { alpha: false });
      if (!ctx) return;
      canvas.width = Math.floor(viewport.width * dpr);
      canvas.height = Math.floor(viewport.height * dpr);
      canvas.style.width = `${Math.floor(viewport.width)}px`;
      canvas.style.height = `${Math.floor(viewport.height)}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      activeRenderTask = pdfPage.render({ canvasContext: ctx, viewport });
      renderTaskRef.current = activeRenderTask;
      await activeRenderTask.promise;
      if (requestId !== requestIdRef.current) return;
      setErr("");
    } catch (error) {
      if (
        requestId !== requestIdRef.current ||
        error?.name === "RenderingCancelledException" ||
        /cancelled/i.test(String(error?.message || ""))
      ) {
        return;
      }
      setErr("Could not load PDF.");
    } finally {
      if (requestId === requestIdRef.current) {
        setBusy(false);
      }
      if (renderTaskRef.current === activeRenderTask) {
        renderTaskRef.current = null;
      }
    }
  }, [cancelRenderTask]);

  React.useEffect(() => {
    if (!open || !src) {
      requestIdRef.current += 1;
      setPdfDoc(null);
      setNumPages(0);
      setPage(1);
      setErr("");
      setBusy(false);
      cancelRenderTask();
      cleanupLoadingTask();
      cleanupLoadedPdf();
      return;
    }

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setErr("");
    setBusy(true);
    setPdfDoc(null);
    setNumPages(0);
    setPage(1);

    (async () => {
      try {
        const task = getDocument({ url: src, withCredentials: false });
        loadingTaskRef.current = task;
        const pdf = await task.promise;
        if (requestId !== requestIdRef.current) {
          if (pdf?.destroy) {
            try {
              await pdf.destroy();
            } catch {}
          }
          return;
        }
        await cleanupLoadedPdf();
        pdfDocRef.current = pdf;
        setPdfDoc(pdf);
        setNumPages(pdf.numPages);
        setErr("");
      } catch (error) {
        if (
          requestId !== requestIdRef.current ||
          /cancelled/i.test(String(error?.message || ""))
        ) {
          return;
        }
        setErr("Could not load PDF.");
      } finally {
        if (loadingTaskRef.current && requestId === requestIdRef.current) {
          loadingTaskRef.current = null;
          setBusy(false);
        }
      }
    })();

    return () => {
      if (requestIdRef.current === requestId) {
        requestIdRef.current += 1;
      }
      cancelRenderTask();
      cleanupLoadingTask();
      cleanupLoadedPdf();
    };
  }, [open, src, cancelRenderTask, cleanupLoadingTask, cleanupLoadedPdf]);

  React.useEffect(() => {
    if (!open || !pdfDoc) return;

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    (async () => {
      if (page < 1 || page > pdfDoc.numPages) return;
      await renderPage(pdfDoc, page, scale, requestId);
    })();

    return () => {
      cancelRenderTask();
    };
  }, [open, pdfDoc, page, scale, renderPage, cancelRenderTask]);

  const zoomIn = () => setScale((current) => Math.min(current + 0.25, 4));
  const zoomOut = () => setScale((current) => Math.max(current - 0.25, 0.25));
  const prev = () => setPage((current) => Math.max(current - 1, 1));
  const next = () => setPage((current) => Math.min(current + 1, numPages));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      sx={{
        zIndex: (theme) => Math.max(theme.zIndex?.modal || 1300, 9999) + 1000,
      }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {title || fileName || "Preview"}
        <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 0.5 }}>
          <IconButton onClick={zoomOut} size="small" disabled={busy} title="Zoom out">
            <ZoomOutIcon fontSize="small" />
          </IconButton>
          <IconButton onClick={zoomIn} size="small" disabled={busy} title="Zoom in">
            <ZoomInIcon fontSize="small" />
          </IconButton>
          <IconButton onClick={prev} size="small" disabled={busy || page <= 1} title="Previous">
            <NavigateBeforeIcon fontSize="small" />
          </IconButton>
          <Typography variant="body2" sx={{ minWidth: 80, textAlign: "center" }}>
            {page} / {numPages || "-"}
          </Typography>
          <IconButton onClick={next} size="small" disabled={busy || page >= numPages} title="Next">
            <NavigateNextIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            title="Download"
            onClick={downloadProof}
          >
            <FileDownloadOutlinedIcon fontSize="small" />
          </IconButton>
          <IconButton onClick={onClose} size="small" title="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent
        dividers
        sx={{ display: "grid", placeItems: "center", bgcolor: "#f7f7f7", minHeight: 420 }}
      >
        {err ? (
          <Box sx={{ textAlign: "center", display: "grid", gap: 1.25, placeItems: "center", maxWidth: 420 }}>
            <PictureAsPdfOutlinedIcon color="error" sx={{ fontSize: 48 }} />
            <Typography color="error" sx={{ mb: 1 }}>
              {err}
            </Typography>
            <Button
              variant="contained"
              size="small"
              onClick={() => window.open(src, "_blank", "noopener,noreferrer")}
              sx={{ textTransform: "none", width: "100%", maxWidth: 280 }}
            >
              Open PDF
            </Button>
          </Box>
        ) : (
          <canvas ref={canvasRef} />
        )}
      </DialogContent>
    </Dialog>
  );
}
